import { useCallback, useRef } from 'react'

// Pointer handlers a long-press target spreads onto its element, plus a
// consumeClick() the element's onClick calls to swallow the click that a
// long-press would otherwise emit (so a press-and-hold doesn't also navigate /
// toggle).
export interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: () => void
  onPointerLeave: () => void
  onPointerCancel: () => void
  onContextMenu: (e: React.MouseEvent) => void
}

export interface UseLongPressOptions {
  // Hold duration before the long-press fires (ms).
  delayMs?: number
  // Movement (px) that cancels the press — a scroll/drag, not a hold.
  moveThreshold?: number
}

// useLongPress detects a press-and-hold on a single element across touch, pen,
// and mouse (unified via Pointer Events). It calls onLongPress once the hold
// crosses delayMs without moving past moveThreshold, then arms consumeClick()
// so the trailing click can be swallowed by the element's onClick. A quick tap
// (released before delayMs) never fires and never consumes — the normal click
// proceeds. A right/middle mouse button is ignored. Touch long-press fires the
// platform context menu / callout; onContextMenu suppresses it for non-mouse
// pointers (desktop right-click is left intact).
export function useLongPress(
  onLongPress: () => void,
  opts?: UseLongPressOptions,
): { handlers: LongPressHandlers; consumeClick: () => boolean } {
  const delayMs = opts?.delayMs ?? 450
  const moveThreshold = opts?.moveThreshold ?? 10

  const timer = useRef<number | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)
  const pointerType = useRef<string>('')

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    start.current = null
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only the primary mouse button arms a long-press; touch/pen always do.
      if (e.pointerType === 'mouse' && e.button !== 0) return
      fired.current = false
      pointerType.current = e.pointerType
      start.current = { x: e.clientX, y: e.clientY }
      clear()
      timer.current = window.setTimeout(() => {
        fired.current = true
        timer.current = null
        onLongPress()
      }, delayMs)
    },
    [clear, delayMs, onLongPress],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!start.current || timer.current === null) return
      const dx = e.clientX - start.current.x
      const dy = e.clientY - start.current.y
      if (dx * dx + dy * dy > moveThreshold * moveThreshold) clear()
    },
    [clear, moveThreshold],
  )

  const onContextMenu = useCallback((e: React.MouseEvent) => {
    // Suppress the touch/pen long-press callout so it doesn't fight the gesture;
    // leave the desktop right-click menu alone.
    if (pointerType.current !== 'mouse') e.preventDefault()
  }, [])

  const consumeClick = useCallback(() => {
    if (fired.current) {
      fired.current = false
      return true
    }
    return false
  }, [])

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerCancel: clear,
      onContextMenu,
    },
    consumeClick,
  }
}
